<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { onBeforeMount, ref } from "vue";
import {
  HousingApplication,
  HousingFinderInput,
  HousingSiteDto,
  HousingSiteResult,
  HousingSiteResultType,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { useInitialFinderInputsStore } from "../../stores/InititalFinderInputsStore";
import Finder from "../pages/Finder.vue";

const props = defineProps<{
  application: HousingApplication;
}>();

/** The guid of the application. */
const applicationGuid = ref<string>(props.application.guid as string);
/** Used to rerender the finder when the inputs are fetched. */
const inputsKey = ref<number>(0);
/** The application associated with the given guid. */
const housingApplication = ref<HousingApplication>(props.application);
/** The inputs associated with the application. */
const inputs = ref<HousingFinderInput>();
/** A reference to the Finder child component instance. This allows us to call any methods exposed in Finder.vue*/
const finder = ref();
const previousFinderInputString = ref<string>();

const sitesSelected = ref<string[]>([]);
const housingSites = ref<HousingSiteDto[]>([]);

const emit = defineEmits<{
  (e: "updateSelected", sitesQualifiedFor: HousingSiteResult[]): void;
}>();

// /**
//  * Returns the user to the application page
//  */
// const returnToApplication = () => {
//   router.push("/application");
// };

/**
 * Saves the finder info to the database
 */
const handleSaveFinderInfo = async (finderInput: HousingFinderInput) => {
  if (!applicationGuid.value) {
    showError("Application guid not found");
    return;
  }
  finderInput.housingApplicationGuid = applicationGuid.value;
  await apiClient.finderInput
    .createFinderInput(finderInput)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
    })
    .catch(showError);
};

/**
 * Saves the finder info to the database
 */
const handleUpdateFinderInfo = async (finderInput: HousingFinderInput) => {
  const newFinderInputsString = stringifyFinderInput(finderInput);
  // only save if changed
  if (newFinderInputsString === previousFinderInputString.value) {
    return;
  }

  if (!inputs.value?.guid) {
    showError("Finder input guid not found");
    return;
  }

  finderInput.guid = inputs.value?.guid;

  await apiClient.finderInput
    .updateFinderInput(finderInput)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
    })
    .catch(showError);
};

/**
 * Updates the site results in the application
 */
const updateSiteResultsInApplication = async (
  sitesQualifiedFor: HousingSiteResult[],
  isRecalculation = false,
) => {
  if (!housingApplication.value) {
    showError("Housing application not found");
    return;
  }

  if (!isRecalculation) {
    // Explicit selection: mark sites as Preferred/Qualified based on user picks
    sitesQualifiedFor.forEach((site) => {
      if (site.resultType === HousingSiteResultType.NO_OFFER) {
        return;
      } else if (site.siteCode && sitesSelected.value.includes(site.siteCode)) {
        site.resultType = HousingSiteResultType.PREFERRED;
      } else {
        site.resultType = HousingSiteResultType.QUALIFIED;
      }
    });
  }

  // only save if changed
  const newSitesString = stringifySitesQualifiedFor(sitesQualifiedFor);
  if (newSitesString === previousSitesString.value) {
    return;
  }

  await apiClient.housingApplication
    .updateSiteResults(
      housingApplication.value.guid,
      isRecalculation,
      sitesQualifiedFor,
    )
    .then((dto) => {
      housingApplication.value = dto;
      previousSitesString.value = stringifySitesQualifiedFor(dto.siteResults);
      if (dto.siteResults) {
        emit("updateSelected", dto.siteResults);
      }
      computePreferences();
    })
    .catch(showError);
};

const stringifySitesQualifiedFor = (
  sitesQualifiedFor: HousingSiteResult[] | null | undefined,
) => {
  if (!sitesQualifiedFor) {
    return "";
  }
  return JSON.stringify(sitesQualifiedFor);
};

const stringifyFinderInput = (finderInput: HousingFinderInput) => {
  return JSON.stringify(finderInput, (key, value) => {
    if (key === "searchDate") {
      return undefined;
    }
    return value;
  });
};

const previousSitesString = ref<string>(
  stringifySitesQualifiedFor(props.application.siteResults),
);

const initialFinderInputsStore = useInitialFinderInputsStore();
/**
 * Gets the finder inputs associated with the given application guid
 */
const getUsersFinderInputs = async () => {
  if (!applicationGuid.value) {
    return;
  }
  // is there anything in the store?
  if (initialFinderInputsStore.initialInputs) {
    inputs.value = initialFinderInputsStore.getFinderInputs();
    previousFinderInputString.value = stringifyFinderInput(
      initialFinderInputsStore.initialInputs,
    );

    await handleSaveFinderInfo(initialFinderInputsStore.initialInputs);

    // delete the store
    initialFinderInputsStore.resetInitialInputs();
    return;
  }

  await apiClient.finderInput
    .getFinderInputForHousingApplication(applicationGuid.value)
    .then((finderInput) => {
      inputs.value = finderInput;
      previousFinderInputString.value = stringifyFinderInput(finderInput);
    })
    .catch((error) => {
      if (error.message.includes("Not Found")) {
        return;
      } else {
        showError(error);
      }
    });
};

// /**
//  * Gets the application associated with the given application guid
//  */
// const getApplication = async () => {
//   await apiClient.housingApplication
//     .getHousingApplication(applicationGuid.value)
//     .then((dto) => {
//       housingApplicationDto.value = dto;
//     })
//     .catch(handleAPIError);
// };

/**
 * This method iterates through the site results and updates the site preferences
 */
const computePreferences = () => {
  sitesSelected.value = housingApplication.value?.siteResults
    ?.filter((site) => site.resultType === HousingSiteResultType.PREFERRED)
    .map((site) => site.siteCode) as string[];
};

/**
 * Calls updateSiteResultsInApplication with the current siteResults
 * @param selected - An array of selected site codes
 */
const handleConfirmSelection = async () => {
  if (housingApplication.value?.siteResults) {
    await updateSiteResultsInApplication(housingApplication.value?.siteResults)
      .then(() => {
        ElMessage.success("Selection Updated");
      })
      .catch(showError);
  }
};

/**
 * Sets the value of the sites selected
 */
const updateSitesSelected = (sites: string[]) => {
  sitesSelected.value = sites;
};

onBeforeMount(async () => {
  await Promise.all([getUsersFinderInputs()]);
  computePreferences();

  inputsKey.value++;
});
</script>

<template>
  <div class="ApplicationFinderLayout">
    <Finder
      :key="inputsKey"
      ref="finder"
      :inputs="inputs"
      :application="application"
      :favourite-sites="sitesSelected"
      :is-read-only="application.status !== 'Draft'"
      @save-finder-info="handleSaveFinderInfo"
      @update-finder-info="handleUpdateFinderInfo"
      @set-sites="(sites: HousingSiteDto[]) => (housingSites = sites)"
      @update-sites-qualified-for="
        (sites: HousingSiteResult[]) =>
          updateSiteResultsInApplication(sites, true)
      "
      @update-selection="($event: string[]) => updateSitesSelected($event)"
      @confirm-selection="handleConfirmSelection"
    />
  </div>
</template>

<style lang="less">
.ApplicationFinderLayout {
  height: 100%;

  &:has(.calculator-box) {
    display: flex;
    justify-content: center;
  }

  .FinderPage {
    height: 100%;
    .results {
      height: 100%;
    }
  }
  .calculator-box {
    margin-top: 1em;
  }
}
</style>
